// Admin: find a device by Unique Number, and assign it to a user / location /
// room. The React equivalent of the old Windows "Devices" form.
//
// ── The assignment chain ───────────────────────────────────────────────────
// A device is not usable until FOUR things line up, and doing only the first is
// the classic half-assignment that leaves a device nobody can see:
//
//   1. DevicesInstalled row      MasterUserId, LocationID, RoomId, DeviceName
//   2. user -> location mapping  or the user cannot open the location
//   3. user -> room mapping      or the room is invisible inside it
//   4. user -> device mapping    or the device is invisible inside the room
//
// So `assignDevice` always writes all four, and treats "already mapped" as
// success rather than an error — the endpoints are additive and re-running an
// assignment is a normal thing for an admin to do.
//
// ⚠️ ROOM 0 MEANS "COMMON DEVICE". A device with RoomId 0 belongs to the
// location rather than to any room, and is what the app lists under Common
// Devices. It is a real value, not a missing one, so it must never be treated
// as "no room chosen yet".

import {
  post, getDeviceDetailFresh, getDeviceInstalledById, getLocations, getRooms,
  addLocation, addRoom,
  addUserLocationMapping, addLocationRoomsMapping, addUserDeviceMapping,
  updateDeviceInstalled, getDevicesByRoom, getCommonDevices,
  deleteUserDeviceMapping,
} from '../api/api';

/** RoomId used for a device that belongs to the location, not to a room. */
export const COMMON_ROOM_ID = 0;

const rows = (res) => (res && Array.isArray(res.data) ? res.data : []);

/** Every user, for the Master User picker. 450+ rows, so filter in the UI. */
export const getAllUsers = () => post('GetAllUserDetails', {});

export const userLabel = (u) => {
  const name = [u.Surname, u.Name, u.FatherHusband].filter(Boolean).join(' ').trim();
  return name || `User ${u.UserId}`;
};

/**
 * Look a device up by Unique Number.
 *
 * -> { found, device, assigned }
 *
 * `assigned` is the question the admin actually has: a row can EXIST (the
 * device was manufactured and registered) while being attached to no location,
 * which is a different state from "unknown number" and needs different wording.
 *
 * ⚠️ `getDeviceDetailFresh`, NEVER the local-first `getDeviceDetail`. On a
 * native build that cache is the SIGNED-IN user's sync, so for a device
 * belonging to somebody else — which is every device this page is opened for —
 * it answers with a row this admin's own app happens to hold, stale or absent.
 * LocationName / RoomName then disagree with the LocationID / RoomId the
 * assignment is built from, and the assignment is written against the wrong
 * "before".
 */
export async function findDevice(uniqueNumber) {
  const uno = String(uniqueNumber || '').trim();
  if (!uno) return { found: false, device: null, assigned: false };
  const device = rows(await getDeviceDetailFresh(uno).catch(() => null))[0] || null;
  if (!device) return { found: false, device: null, assigned: false };
  const locationId = device.LocationID ?? device.LocationId;
  return {
    found: true,
    device: { ...device, ...(await installExtras(device.ID)) },
    assigned: locationId != null && Number(locationId) > 0,
  };
}

/**
 * The three columns the detail query does not select, read from the raw
 * DevicesInstalled row.
 *
 * `WifiName` is the one that matters: it is the SSID the device is joined to
 * RIGHT NOW — the firmware sends it with every status update — and it is the
 * first thing anyone asks when a device has stopped answering. None of the
 * device-detail endpoints return it.
 *
 * ⚠️ NAMED FIELDS ONLY, never a spread of the row: it also carries
 * `WifiPassword` in clear, and this object ends up in component state.
 *
 * Best-effort — an extra call must not turn a working lookup into a failure, so
 * a miss yields `{}` and the caller simply has no SSID to show.
 */
async function installExtras(installedId) {
  if (installedId == null) return {};
  const row = rows(await getDeviceInstalledById(installedId).catch(() => null))[0];
  if (!row) return {};
  return {
    WifiName: row.WifiName || '',
    // When the device last spoke to the broker, as against LastUpdatedDateTime
    // which any write to the row moves.
    LastMqttPing: row.LastMqttPing || '',
    LastStatusTime: row.LastStatusTime || '',
  };
}

/** Create a location for a user and map them to it. -> LocationID */
export async function createLocation(userId, locationName) {
  const res = await addLocation({ LocationName: locationName });
  const id = res?.data?.insertId ?? res?.data?.[0]?.insertId;
  if (id == null) throw new Error('The server did not return the new location id.');
  await addUserLocationMapping(userId, id).catch(() => {});
  return id;
}

/** Create a room in a location and map the user to it. -> RoomID */
export async function createRoom(userId, locationId, roomName) {
  const res = await addRoom(roomName, locationId);
  const id = res?.data?.insertId ?? res?.data?.[0]?.insertId;
  if (id == null) throw new Error('The server did not return the new room id.');
  await addLocationRoomsMapping(userId, locationId, id).catch(() => {});
  return id;
}

export const locationsOf = async (userId) => rows(await getLocations(userId).catch(() => null));
export const roomsOf = async (userId, locationId) =>
  rows(await getRooms(userId, locationId).catch(() => null));

/**
 * The devices in one room — or, for COMMON_ROOM_ID, the location's common
 * devices, which live in a different table and need a different call. Selecting
 * "Common devices" in a room dropdown must not fetch room 0.
 *
 * The location goes to the room call as well: without it the room endpoint hides
 * whole device types, and this page's whole job is to explain where a device is
 * — showing a room that omits the very device being looked up is the worst
 * possible answer here. See core/devices/roomMerge.js.
 */
export async function devicesIn(userId, locationId, roomId) {
  if (Number(roomId) === COMMON_ROOM_ID) {
    return rows(await getCommonDevices(userId, locationId).catch(() => null));
  }
  return rows(await getDevicesByRoom(userId, roomId, locationId).catch(() => null));
}

/**
 * Write the install row and every mapping the user needs to actually see it.
 *
 * @param device        the row from findDevice (for DeviceInstalledId)
 * @param roomId        a RoomID, or COMMON_ROOM_ID for a location-level device
 *
 * ⚠️ Writes MasterUserId. The row ALSO carries DeviceMasterUserId and
 * LocationMasterUserId, which the old form exposes as separate fields; those are
 * left alone rather than guessed at, since setting the wrong one silently moves
 * ownership.
 */
export async function assignDevice({
  device, uniqueNumber, userId, locationId, roomId, deviceName,
}) {
  const data = {
    MasterUserId: Number(userId),
    LocationID: Number(locationId),
    RoomId: Number(roomId),
  };
  if (deviceName != null && String(deviceName).trim()) {
    data.DeviceName = String(deviceName).trim();
  }
  await updateDeviceInstalled(uniqueNumber, data);

  // The three mappings. Each is additive and may already exist, so a rejection
  // here is not a failure of the assignment — the row above is what matters.
  await addUserLocationMapping(userId, locationId).catch(() => {});
  if (Number(roomId) !== COMMON_ROOM_ID) {
    await addLocationRoomsMapping(userId, locationId, roomId).catch(() => {});
  }
  const installedId = device?.ID ?? device?.DeviceInstalledID ?? device?.DeviceInstalledId;
  if (installedId != null) {
    await addUserDeviceMapping(userId, installedId).catch(() => {});
  }
  return true;
}

/** Change the device's type (DeviceID on the install row). */
export const setDeviceType = (uniqueNumber, deviceId) =>
  updateDeviceInstalled(uniqueNumber, { DeviceID: Number(deviceId) });

/**
 * Detach a device from its owner, location and room.
 *
 * ⚠️ THE MIRROR OF assignDevice, AND IT HAS TO UNDO ALL OF IT. Clearing only
 * MasterUserId (what the app's own "remove device" does) leaves LocationID and
 * RoomId pointing at a site the device no longer belongs to, and leaves the
 * user->device mapping in place — so the device keeps appearing in that room
 * for that user while reporting no owner. Half-unassigned is harder to diagnose
 * than either state.
 *
 * The location and room mappings are deliberately NOT removed: they belong to
 * the user's own access to that site and other devices may still rely on them.
 */
export async function unassignDeviceFully(device, uniqueNumber) {
  await updateDeviceInstalled(uniqueNumber, {
    MasterUserId: 0,
    LocationID: 0,
    RoomId: 0,
  });
  const installedId = device?.ID ?? device?.DeviceInstalledID ?? device?.DeviceInstalledId;
  const owner = device?.MasterUserId ?? device?.DeviceMasterUserId;
  if (installedId != null && owner != null) {
    await deleteUserDeviceMapping(installedId, owner).catch(() => {});
  }
  return true;
}

// ── Who can see this device ────────────────────────────────────────────────
// GetAllUsersByUniqueNumber returns UserIds and nothing else, so names are
// resolved against the full user list the page already holds.
export async function deviceUserIds(uniqueNumber) {
  const list = rows(await post('GetAllUsersByUniqueNumber', { UniqueNumber: uniqueNumber })
    .catch(() => null));
  return list.map((r) => Number(r.UserId)).filter((n) => Number.isFinite(n));
}

/**
 * Give a user access to this device.
 *
 * ⚠️ THE DEVICE MAPPING ALONE IS NOT ACCESS. A user who is mapped to the device
 * but not to its location cannot open the location, so the room never renders
 * and the device is invisible — the mapping exists and the user still cannot
 * see the thing. Location and room go with it, every time.
 *
 * A common device (RoomId 0) has no room to map, and asking for room 0 would
 * write a mapping to a room that does not exist.
 */
export async function grantUserAccess(device, userId) {
  const locationId = device?.LocationID ?? device?.LocationId;
  const roomId = device?.RoomId;
  const installedId = device?.ID ?? device?.DeviceInstalledID ?? device?.DeviceInstalledId;
  if (installedId == null) throw new Error('This device has no installed id.');

  if (locationId != null && Number(locationId) > 0) {
    await addUserLocationMapping(userId, locationId).catch(() => {});
    if (roomId != null && Number(roomId) !== COMMON_ROOM_ID) {
      await addLocationRoomsMapping(userId, locationId, roomId).catch(() => {});
    }
  }
  await addUserDeviceMapping(userId, installedId);
  return true;
}

/**
 * Take a user's access to this device away.
 *
 * Only the DEVICE mapping is removed. Their location and room mappings are left
 * alone deliberately: those are their access to the whole site, and other
 * devices in that room almost certainly depend on them — removing them here
 * would silently empty rooms that have nothing to do with this device.
 */
export async function revokeUserAccess(device, userId) {
  const installedId = device?.ID ?? device?.DeviceInstalledID ?? device?.DeviceInstalledId;
  if (installedId == null) throw new Error('This device has no installed id.');
  await deleteUserDeviceMapping(installedId, userId);
  return true;
}
